import { cartItemOptionLines } from "@/components/cart/cart-item-option-lines"
import { ImageWithFallback } from "@/components/image/ImageWithFallback"
import { BottomSheet } from "@/components/modals/BottomSheet"
import { OrderItemLine, OrderItemList } from "@/components/orders/OrderItemLine"
import { Button, Card, CountBadge, DescriptionList, Icon, KeyValueRow, Price, SheetBody, SheetFooter, SheetHeader, Surface, Text } from "@/components/ui"
import { Chevron } from "@/icons"
import { Cart, CartItem } from "@/types"
import { useState } from "react"

const MAX_THUMBS = 3

/**
 * Compact "selected items" strip for the review step: item thumbnails with
 * quantity badges + a "Detalhes" affordance. Tapping anywhere opens the
 * bottom sheet with the full item breakdown.
 */
export function SelectedItemsSummary({ cart }: { cart: Cart }) {
  const [open, setOpen] = useState(false)
  const visible = cart.items.slice(0, MAX_THUMBS)
  const overflow = cart.items.length - visible.length

  return (
    <>
      <Button
        variant="ghost"
        tone="neutral"
        size="auto"
        align="between"
        fullWidth
        onClick={() => setOpen(true)}
        aria-label="Ver todos os itens selecionados"
        className="gap-3"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden p-1 -m-1">
          {visible.map((cartItem) => (
            <ItemThumb key={cartItem.id} cartItem={cartItem} />
          ))}
          {overflow > 0 && (
            <Card variant="inset" padding="none" className="flex h-14 min-w-10 items-center justify-center">
              <Text as="span" size="sm" weight="bold" tone="muted">
                +{overflow}
              </Text>
            </Card>
          )}
        </div>
        <Text as="span" size="sm" weight="semibold" tone="muted" className="inline-flex shrink-0 items-center gap-1">
          Detalhes <Icon as={Chevron} size="sm" />
        </Text>
      </Button>
      <SelectedItemsSheet open={open} onOpenChange={setOpen} cart={cart} />
    </>
  )
}

function ItemThumb({ cartItem }: { cartItem: CartItem }) {
  return (
    <span className="relative inline-block h-14 w-14 shrink-0">
      <Surface surface="page" as="span" className="block h-full w-full overflow-hidden rounded-card border border-border">
        <ImageWithFallback
          width={112}
          height={112}
          alt={cartItem.item.name}
          src={cartItem.item.image?.URL}
          crc={cartItem.item.image?.["CRC-32"]}
          fit="contain"
          className="h-full w-full"
        />
      </Surface>
      <CountBadge count={cartItem.quantity} tone="neutral" bordered className="absolute -bottom-1 -left-1" />
    </span>
  )
}

/**
 * Bottom sheet listing every selected item with options and prices.
 */
function SelectedItemsSheet({
  open,
  onOpenChange,
  cart,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  cart: Cart
}) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <SheetHeader title="Itens selecionados" onClose={() => onOpenChange(false)} />

      <SheetBody maxHeight="md" className="pb-4">
        <OrderItemList>
          {cart.items.map((cartItem) => (
            <OrderItemLine
              key={cartItem.id}
              name={cartItem.item.name}
              quantity={cartItem.quantity}
              quantityDisplay="always"
              price={cartItem.itemTotalPrice}
              priceTone="emphasis"
              image={{ src: cartItem.item.image?.URL, crc: cartItem.item.image?.["CRC-32"] }}
              options={cartItemOptionLines(cartItem)}
              note={cartItem.specialInstructions}
            />
          ))}
        </OrderItemList>
      </SheetBody>

      <SheetFooter bordered className="pt-4">
        <DescriptionList>
          <KeyValueRow
            label={
              <Text as="span" weight="semibold">
                Subtotal dos itens
              </Text>
            }
            value={<Price value={cart.totalPrice} weight="bold" tone="default" />}
            size="base"
          />
        </DescriptionList>
      </SheetFooter>
    </BottomSheet>
  )
}
